import React, { useMemo, useState } from 'react'
import { IOrganizationPupils } from '@app/interfaces/pupil/organization-pupils'
import { Button, Card, Table, TableColumnType } from 'antd'
import { useTranslation } from 'react-i18next'
import { AddPupilPaymentModal } from './add-pupil-payment-modal'
import { formatTiyinToSumLabel } from '@app/utils/format-tiyin-to-sum-label'
import { useParams } from 'react-router'
import { useGetOrganizationQuery } from '@app/store/rtk/api/organization/organization-api'
import useLocalization from '@app/hooks/useLocalization'
import {
  useGetPupilBillingQuery,
  useGetPupilPaymentHistoryQuery,
} from '@app/store/rtk/api/billing/pupil-billing'
import { IPupilPaymentBillingItem } from '@app/interfaces/kms-billing'
import { getMonthIndex } from '@app/utils/date'

type Props = {
  _pupil: IOrganizationPupils
}

export const OrganizationPupilBilling = ({ _pupil }: Props) => {
  const { t } = useTranslation()
  const [addPaymentModalVisible, setAddPaymentModalVisible] = useState(false)
  const { id: organizationId } = useParams()
  const { localizeData } = useLocalization()

  const { data: organization } = useGetOrganizationQuery(Number(organizationId))
  const { data: pupilBillingData, isLoading: pupilBillingDataLoading } =
    useGetPupilBillingQuery(_pupil?.pupil?.id)
  const { data: paymentsData, isLoading: paymentsDataLoading } =
    useGetPupilPaymentHistoryQuery(
      {
        pageNumber: 0,
        pageSize: 1000,
        pupilBillingId: pupilBillingData?.response?.id,
        status: 'SUCCEEDED',
      },
      {
        skip: !pupilBillingData?.response?.id,
      },
    )

  console.log('_pupil on billing page: ', _pupil)

  const infoColumns: TableColumnType<IPupilPaymentBillingItem>[] = [
    {
      title: `1. ${t('pupils.billing.period')}`,
      key: 'period',
      width: 150,
      dataIndex: 'period',
      render: (text, record) => {
        const monthIndex = getMonthIndex(record.period, 'yyyy-MM')
        return monthIndex ? t(`monthIndex.${monthIndex}`) : ''
      },
    },
    {
      title: `2. ${t('pupils.billing.beginningBalance')}`,
      key: 'beginningBalance',
      width: 230,
      dataIndex: 'beginningBalance',
      align: 'right',
      render: (text, record) => {
        return formatTiyinToSumLabel(text)
      },
    },
    {
      title: `3. ${t('pupils.billing.calculatedDays')}`,
      key: 'calculatedDays',
      width: 150,
      dataIndex: 'calculatedDay',
      align: 'center',
    },
    {
      title: `4. ${t('pupils.billing.calculatedAmount')}`,
      key: 'calculatedAmount',
      width: 200,
      dataIndex: 'calculatedAmount',
      align: 'right',
      render: (text, record) => {
        return formatTiyinToSumLabel(text)
      },
    },
    {
      title: `5. ${t('pupils.billing.paymentAmountLeft')}`,
      key: 'paymentAmountLeft',
      width: 200,
      dataIndex: 'paymentAmountLeft',
      align: 'right',
      render: (text, record) => {
        return formatTiyinToSumLabel(text)
      },
    },
    {
      title: `6. ${t('pupils.billing.paidAmount')}`,
      key: 'paidAmount',
      width: 200,
      dataIndex: 'paidAmount',
      align: 'right',
      render: (text, record) => {
        return formatTiyinToSumLabel(text)
      },
    },
    {
      title: `7. ${t('pupils.billing.balance')}`,
      key: 'currentBalance',
      width: 200,
      dataIndex: 'currentBalance',
      align: 'right',
      render: (text, record) => {
        return formatTiyinToSumLabel(text)
      },
    },
  ]

  const paymentColumns = [
    {
      title: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '40px',
          }}>
          <strong>{t('pupils.billing.paymentsHistory')}</strong>
          <Button
            style={{
              position: 'absolute',
              right: '24px',
            }}
            type="primary"
            onClick={() => setAddPaymentModalVisible(true)}>
            {t('pupils.billing.addPayment')}
          </Button>
        </div>
      ),
      children: [
        {
          title: '№',
          dataIndex: 'index',
          key: 'id',
          width: 50,
          align: 'center',
        },
        // {
        //   title: t('pupils.billing.operationType'),
        //   key: 'operationType',
        //   width: 150,
        //   dataIndex: 'operationType',
        // },
        // {
        //   title: t('pupils.billing.operationDate'),
        //   key: 'operationDate',
        //   width: 200,
        //   dataIndex: 'operationDate',
        //   align: 'right',
        //   render: (text, record) => {
        //     return formatTiyinToSumLabel(text)
        //   },
        // },
        {
          title: t('pupils.billing.paidAmount'),
          key: 'paidAmount',
          width: 200,
          dataIndex: 'paidAmount',
          align: 'right',
          render: (text, record) => {
            return formatTiyinToSumLabel(text)
          },
        },
        {
          title: t('pupils.billing.paymentPurpose'),
          key: 'purpose',
          width: 200,
          dataIndex: 'purpose',
          align: 'right',
          render: (text, record) => {
            return formatTiyinToSumLabel(text)
          },
        },
        {
          title: t('pupils.billing.transactionAmount'),
          key: 'transactionAmount',
          width: 200,
          dataIndex: 'transactionAmount',
          align: 'right',
          render: (text, record) => {
            return formatTiyinToSumLabel(text)
          },
        },
        {
          title: t('pupils.billing.transactionDate'),
          key: 'transactionDate',
          width: 200,
          dataIndex: 'transactionDate',
          align: 'right',
          render: (text, record) => {
            return formatTiyinToSumLabel(text)
          },
        },
      ],
    },
  ]

  const columnsData = useMemo(() => {
    const totalRow = {
      index: t('general.total'),
      paidAmount: paymentsData?.reduce((acc, i) => acc + i.amount, 0) ?? 0,
      transactionAmount: 0, // TODO qaysi polyadan olish noaniq
    }
    const rows =
      paymentsData?.map((item, index) => {
        return {
          index: index + 1,
          id: item.id,
          operationType: '',
          operationDate: item.transactionDate,
          transactionAmount: 0, // TODO qaysi polyadan olish noaniq
          purpose: item.detail,
          paidAmount: item.amount,
          transactionDate: item.transactionDate,
        }
      }) ?? []
    return rows.length ? [...rows, totalRow] : []
  }, [paymentsData])

  const pupilBillingPrice =
    _pupil.pupil?.pupilPaymentType?.value === 0
      ? 0
      : _pupil.pupil?.pupilPaymentType?.value === 71
      ? organization?.organizationPaymentPrice?.discountedValue
      : organization?.organizationPaymentPrice?.value

  return (
    <>
      <Card
        title={`${_pupil?.pupil?.fullName ?? ''}`}
        extra={
          <p>
            <span>{t('pupils.billing.balance')}:</span>
            <span>
              {formatTiyinToSumLabel(
                pupilBillingData?.response?.currentBalance ?? 0,
              )}
            </span>
          </p>
        }>
        <div
          style={{
            padding: '0px 24px',
          }}>
          <ul style={{ listStyle: 'none', padding: '0px' }}>
            <li>
              <strong>{t('pupils.billing.area')}</strong>:{' '}
              {localizeData(organization?.locality.data)?.name}
            </li>
            <li>
              <strong>{t('pupils.billing.tariff')}</strong>:{' '}
              {localizeData(organization?.organizationSchedule.data)?.name}
            </li>
            <li>
              <strong>{t('pupils.billing.fixedAmount')}</strong>:{' '}
              {formatTiyinToSumLabel(
                organization?.organizationPaymentPrice?.value ?? 0,
              )}
            </li>
            <li>
              <strong>{t('pupils.billing.discount')}</strong>:{' '}
              {_pupil.pupil.pupilPaymentType?.value}%
            </li>
            <li>
              <strong>{t('pupils.billing.paymentAmount')}</strong>:{' '}
              {formatTiyinToSumLabel(pupilBillingPrice ?? 0)}
            </li>
          </ul>
        </div>

        <div style={{ padding: '10px 24px' }}>
          <Table
            columns={infoColumns}
            pagination={false}
            loading={pupilBillingDataLoading}
            dataSource={
              pupilBillingData?.response ? [pupilBillingData.response] : []
            }
            locale={{
              emptyText: (
                <div
                  style={{
                    padding: '40px 0',
                  }}>
                  {t('general.noData')}
                </div>
              ),
            }}
          />
        </div>
        <br />
        <div style={{ padding: '10px 24px 24px' }}>
          <Table
            columns={paymentColumns}
            pagination={false}
            loading={paymentsDataLoading}
            dataSource={columnsData}
            locale={{
              emptyText: (
                <div
                  style={{
                    padding: '40px 0',
                  }}>
                  {t('general.noData')}
                </div>
              ),
            }}
          />
        </div>
      </Card>

      <AddPupilPaymentModal
        visible={addPaymentModalVisible}
        onCancel={() => setAddPaymentModalVisible(false)}
        pupilId={_pupil?.pupil?.id}
        organizationId={Number(organizationId)}
      />
    </>
  )
}
